import React, { useState, useEffect, useCallback } from 'react';

// Styles CSS injectés dynamiquement
const styles = `
  @keyframes submarine-tilt {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
    }
    25% {
      transform: translateY(-1px) rotate(3deg);
    }
    75% {
      transform: translateY(1px) rotate(-3deg);
    }
  }

  @keyframes submarine-float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-2px);
    }
  }

  @keyframes bubble-rise {
    0% {
      opacity: 0;
      transform: translateY(0) scale(0.5);
    }
    10% {
      opacity: 0.8;
    }
    90% {
      opacity: 0.6;
    }
    100% {
      opacity: 0;
      transform: translateY(-40px) scale(1);
    }
  }

  .submarine-logo {
    display: block;
    animation: submarine-float 3s ease-in-out infinite;
  }

  .submarine-logo.tilting {
    animation: submarine-tilt 2s ease-in-out, submarine-float 3s ease-in-out infinite;
  }

  .submarine-bubble {
    position: absolute;
    background: rgba(96, 165, 250, 0.6);
    border-radius: 50%;
    pointer-events: none;
    animation: bubble-rise 2s ease-out forwards;
  }

  .submarine-bubble::before {
    content: '';
    position: absolute;
    top: 15%;
    left: 20%;
    width: 30%;
    height: 30%;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
  }
`;

const AnimatedLogo = ({ src, alt, className, size = 28, interval = 60000 }) => {
  const [bubbles, setBubbles] = useState([]);
  const [isTilting, setIsTilting] = useState(false);

  // Fonction pour créer des bulles
  const createBubbles = useCallback(() => {
    const newBubbles = [];
    const bubbleCount = 4 + Math.floor(Math.random() * 3); // 4-6 bulles

    for (let i = 0; i < bubbleCount; i++) {
      newBubbles.push({
        id: Date.now() + i,
        size: 3 + Math.random() * 5, // 3-8px
        left: -5 + Math.random() * 15, // Position à gauche du sous-marin (là où sont les bulles sur l'image)
        delay: Math.random() * 800, // Délai entre 0-800ms
      });
    }

    setBubbles(newBubbles);

    // Nettoyer les bulles après l'animation
    setTimeout(() => {
      setBubbles([]);
    }, 3000);
  }, []);

  // Fonction pour déclencher l'animation
  const triggerAnimation = useCallback(() => {
    setIsTilting(true);
    createBubbles();

    // Reset le tilting après l'animation
    setTimeout(() => {
      setIsTilting(false);
    }, 2000);
  }, [createBubbles]);

  // Timer pour déclencher l'animation périodiquement
  useEffect(() => {
    // Déclencher une première fois après un délai initial (1/4 de l'intervalle, min 2s, max 10s)
    const initialDelay = Math.min(10000, Math.max(2000, interval / 4));
    const initialTimeout = setTimeout(() => {
      triggerAnimation();
    }, initialDelay);

    // Puis à l'intervalle spécifié
    const intervalTimer = setInterval(() => {
      triggerAnimation();
    }, interval);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalTimer);
    };
  }, [triggerAnimation, interval]);

  // Injection des styles CSS
  useEffect(() => {
    const styleId = 'animated-logo-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = styles;
      document.head.appendChild(styleElement);
    }

    return () => {
      // Ne pas supprimer les styles au démontage car d'autres instances pourraient les utiliser
    };
  }, []);

  return (
    <div 
      style={{ 
        position: 'relative', 
        width: size, 
        height: size,
        flexShrink: 0
      }}
    >
      <img
        src={src}
        alt={alt}
        className={`submarine-logo ${isTilting ? 'tilting' : ''}`}
        style={{
          width: size,
          height: size,
          objectFit: 'contain'
        }}
      />
      {bubbles.map((bubble) => (
        <span
          key={bubble.id}
          className="submarine-bubble"
          style={{
            width: `${bubble.size}px`,
            height: `${bubble.size}px`,
            left: `${bubble.left}px`,
            top: '50%',
            animationDelay: `${bubble.delay}ms`,
          }}
        />
      ))}
    </div>
  );
};

export default AnimatedLogo;
